import { useEffect, useRef, useState } from 'react';
import { url } from '../utils/confserveur';
import { enTetesPilotageScan, surPageScan } from '../utils/pilotageScan';

/**
 * Le flux est ouvert par `fetch`, pas par axios : l'intercepteur qui
 * rafraîchit le jeton d'accès n'y passe jamais. Hors page de scan, un 401 à
 * la (ré)ouverture veut donc dire « jeton à rafraîchir », pas « fin de
 * séance » : on appelle le même refresh que l'intercepteur, et on rouvre. Si
 * le refresh échoue, le prof est réellement déconnecté : on s'arrête.
 * (Bug de démo du 2026-09-12 : après ~10 min, le téléphone avançait mais la
 * projection, dont le flux s'était rouvert avec un JWT périmé, était morte
 * sans message.)
 */
const rafraichirJeton = async () => {
  try {
    const r = await fetch(`${url}api/token/refresh/`, { method: 'POST', credentials: 'include' });
    return r.ok;
  } catch {
    return false;
  }
};

/**
 * Flux SSE d'une session QCM (GET sessions-qcm/:id/stream/) — implémentation
 * UNIQUE partagée par SessionLive (pilotage/scan prof) et ProjectionQCM.
 *
 * Fait main via fetch + ReadableStream (PAS EventSource : il faut
 * credentials:'include' pour les cookies d'auth). Reconnexion automatique
 * toutes les `retryDelayMs` tant que la session n'est pas terminée ;
 * l'événement serveur `timeout` ferme proprement le flux courant et laisse
 * le retry reconnecter (comportement historique des deux écrans).
 *
 * Le hook est agnostique du métier : chaque événement est parsé puis transmis
 * tel quel à `onEvent(type, data)` — c'est l'écran consommateur qui décide
 * quoi faire de 'question_change' / 'reponse' / 'reveal' / 'session_terminee'.
 * `onTerminated` est appelé sur 'session_terminee', APRÈS onEvent, et le
 * hook s'arrête définitivement.
 *
 * Les callbacks sont lus via des refs rafraîchies à chaque render : les
 * dépendances de l'effet se limitent à [sessionId, enabled, retryDelayMs],
 * donc aucun cycle de reconnexion parasite quand le parent re-render.
 *
 * @returns {{ streamStatus: 'idle'|'connecting'|'open'|'retrying'|'stopped' }}
 */
export function useSessionStream({ sessionId, enabled, onEvent, onTerminated, retryDelayMs = 2000 }) {
  const [streamStatus, setStreamStatus] = useState('idle');

  const onEventRef = useRef(onEvent);
  const onTerminatedRef = useRef(onTerminated);
  onEventRef.current = onEvent;
  onTerminatedRef.current = onTerminated;

  useEffect(() => {
    if (!enabled || !sessionId) {
      setStreamStatus('idle');
      return undefined;
    }

    const controller = new AbortController();
    let stopped = false;

    const connectSSE = async () => {
      try {
        setStreamStatus('connecting');
        const response = await fetch(`${url}sessions-qcm/${sessionId}/stream/`, {
          credentials: 'include',
          // page de scan : le serveur lit le cookie de scan avant le JWT
          headers: enTetesPilotageScan(),
          signal: controller.signal,
        });

        if (!response.ok) {
          console.error('[SSE] HTTP error:', response.status);
          if (response.status === 401 || response.status === 403) {
            // Hors page de scan : un jeton d'accès périmé. Rafraîchi, le flux
            // se rouvre par le retry ; sinon, déconnecté pour de bon.
            if (!surPageScan() && response.status === 401 && await rafraichirJeton()) return;
            // Refusé (appareil détaché, cookie de scan expiré, séance d'un
            // autre, prof déconnecté) : réessayer toutes les deux secondes ne
            // changerait rien. On s'arrête et on prévient comme pour une fin
            // de séance (chantier jeton).
            stopped = true;
            setStreamStatus('stopped');
            onTerminatedRef.current?.();
          }
          return;
        }
        setStreamStatus('open');

        const reader = response.body.getReader();
        const decoder = new TextDecoder();
        let buffer = '';

        while (!stopped) {
          const { done, value } = await reader.read();
          if (done) break;

          buffer += decoder.decode(value, { stream: true });
          const parts = buffer.split('\n\n');
          buffer = parts.pop();

          for (const part of parts) {
            let eventType = 'message';
            let data = '';

            for (const line of part.split('\n')) {
              if (line.startsWith('event:')) {
                eventType = line.slice(6).trim();
              } else if (line.startsWith('data:')) {
                data += line.slice(5).trim();
              }
            }

            if (!data) continue;

            try {
              if (eventType === 'timeout') {
                // fin de flux côté serveur → on sort, le retry reconnecte
                return;
              }
              const parsed = JSON.parse(data);
              onEventRef.current?.(eventType, parsed);
              if (eventType === 'session_terminee') {
                stopped = true;
                setStreamStatus('stopped');
                onTerminatedRef.current?.();
                return;
              }
            } catch (parseErr) {
              console.error('[SSE] JSON parse error:', parseErr);
            }
          }
        }
      } catch (err) {
        if (err?.name !== 'AbortError') {
          console.error('[SSE] Error:', err);
        }
      }
    };

    const connectWithRetry = async () => {
      while (!stopped) {
        await connectSSE();
        if (stopped) break;
        setStreamStatus('retrying');
        await new Promise((r) => setTimeout(r, retryDelayMs));
      }
    };

    connectWithRetry();

    return () => {
      stopped = true;
      controller.abort();
    };
  }, [sessionId, enabled, retryDelayMs]);

  return { streamStatus };
}

export default useSessionStream;
